import * as React from 'react'
import classNames from 'classnames'
import type { SupportedLanguage } from '../../../shared/i18n/i18n.no-deps'
import type { Nullable, Tree } from '../../../shared/lib/typeHelpers.no-deps'
import { getLeaves } from '../../../shared/lib/tree.no-deps'
import { pipe } from '../../../shared/lib/functional/pipe.no-deps'
import { map, uniq } from '../../../shared/lib/functional/array.no-deps'
import { toggleSet } from '../../../shared/lib/functional/set.no-deps'
import { SVG_ChevronDownSmall } from '../svg/SVG_ChevronDownSmall'
import { SVG_Cross } from '../svg/SVG_Cross'
import i18next from 'i18next'
import { SelectBox } from '../../../shared/webcomponents/react-wrappers/SelectBox'
import { useInfiniteLoading } from '../util/infiniteLoading'
import { Result } from '@zaptic-external/ts-monads'

const chunkSize = 100

export type Renderable<T> = {
    id: T
    label: string
    tagline?: string
    leftNode?: React.ReactNode
}

interface Props<T> {
    options: Array<Renderable<T>>
    // The entire renderable object is stored in search tree so we can render
    // it directly without having to perform a lookup in options for every leaf
    searchTree?: Tree<Renderable<T>>
    selectedValue: T
    language: SupportedLanguage
    onChange: (selectedValue: Nullable<T>) => void
    hasErrors?: boolean
    disabled?: boolean
    overlay?: boolean
    placeholder?: string
}

interface State<T> {
    showSearch: boolean
    displayedChunks: number
    searchTerm: string
    expandedCategories: Set<T>
}

export class SearchableDropdown<T extends React.ReactText> extends React.Component<Props<T>, State<T>> {
    public state = {
        showSearch: false,
        displayedChunks: 1,
        searchTerm: '',
        expandedCategories: new Set<T>(),
    }

    private dropdownElement: HTMLElement
    private searchBoxElement: HTMLInputElement
    private optionsElement: HTMLElement

    public componentDidMount = () => {
        this.dropdownElement.addEventListener('focusout', this.onBlur)
    }

    public componentWillUnmount = () => {
        this.dropdownElement.removeEventListener('focusout', this.onBlur)
    }

    public componentDidUpdate(_: Props<T>, prevState: State<T>) {
        if (!prevState.showSearch && this.state.showSearch) {
            this.searchBoxElement?.focus()
        }
    }

    private showTree() {
        if (this.props.searchTree == null) return false
        if (getSelectableDescendantCount(this.props.searchTree) === 0) return false
        return this.state.searchTerm === ''
    }

    private handleOptionsScroll = () => {
        const maximumScrollTop = this.optionsElement.scrollHeight - this.optionsElement.clientHeight
        const isScrolledToBottom = maximumScrollTop === this.optionsElement.scrollTop
        if (isScrolledToBottom) {
            this.setState(({ displayedChunks }) => {
                const { options } = this.props
                if (displayedChunks * chunkSize >= options.length) return { displayedChunks }
                return {
                    displayedChunks: displayedChunks + 1,
                }
            })
        }
    }

    private renderOptions = () => {
        const { options } = this.props
        const { displayedChunks, searchTerm } = this.state

        const filteredOptions = options.filter((option) => {
            // Search by label or tagline (if tagline exist)
            return (
                option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (option?.tagline && option.tagline.toLowerCase().includes(searchTerm.toLowerCase()))
            )
        })

        if (filteredOptions.length === 0) {
            return (
                <div
                    className={classNames('AppCore__ui__SearchableDropdown__noOptions', {
                        overlay: this.props?.overlay,
                    })}
                >
                    {i18next.t('appcore:SearchableDropdown.noResults')}
                </div>
            )
        }

        return filteredOptions
            .slice(0, displayedChunks * chunkSize)
            .map((option) => (
                <Option
                    id={option.id}
                    label={option.label}
                    tagline={option.tagline}
                    leftNode={option.leftNode}
                    isSelected={option.id === this.props.selectedValue}
                    onClick={this.handleOptionClick}
                />
            ))
    }

    private renderSearchableOptions = () => {
        const { showSearch } = this.state
        return (
            <div
                className={classNames('AppCore__ui__SearchableDropdown__searchableOptions', {
                    open: showSearch,
                    overlay: this.props?.overlay,
                })}
                tabIndex={-1}
            >
                {showSearch ? (
                    <div
                        className="AppCore__ui__SearchableDropdown__options"
                        onScrollCapture={this.handleOptionsScroll}
                        ref={(ref) => (this.optionsElement = ref)}
                    >
                        {this.showTree() ? (
                            <TreeInner
                                tree={this.props.searchTree}
                                isTopLevel={true}
                                selectedOptionId={this.props.selectedValue}
                                expandedCategories={this.state.expandedCategories}
                                onCategoryClick={this.onCategoryClick}
                                onOptionClick={this.handleOptionClick}
                            />
                        ) : (
                            this.renderOptions()
                        )}
                    </div>
                ) : null}
            </div>
        )
    }

    private getSelectedOption = () => {
        const { options, selectedValue } = this.props

        if (selectedValue == null) return null

        const selectedOption = options.find((option) => option.id === selectedValue)

        if (!selectedOption) return null

        return selectedOption
    }

    private handleOptionClick = (id: T) => {
        const { onChange, selectedValue } = this.props
        this.scrollOptionsToTop()

        this.setState({
            showSearch: false,
            displayedChunks: 1,
            searchTerm: '',
        })
        onChange(id === selectedValue ? null : id)
    }

    private scrollOptionsToTop = () => (this.optionsElement.scrollTop = 0)

    private handleSelectedClick = () => {
        this.setState((prevState) => {
            if (prevState.showSearch) {
                this.scrollOptionsToTop()
            }

            return {
                showSearch: !prevState.showSearch,
                displayedChunks: 1,
                searchTerm: '',
            }
        })
    }

    private onBlur = (event: FocusEvent) => {
        if (this.dropdownElement == null) return
        if (this.dropdownElement.contains(event.relatedTarget as Node)) return
        if (!this.state.showSearch) return
        this.setState({ showSearch: false })
    }

    private handleSearchTermChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.scrollOptionsToTop()
        this.setState({
            searchTerm: event.target.value,
            displayedChunks: 1,
        })
    }

    private onCategoryClick = (id: T) => {
        const expandedCategories = toggleSet(this.state.expandedCategories, id)
        this.setState({ expandedCategories })
    }

    private onClear = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation()
        this.props.onChange(null)
    }

    private renderPlaceholder() {
        return (
            <div className="AppCore__ui__SearchableDropdown__placeholder">
                {this.props.placeholder ?? i18next.t('appcore:SearchableDropdown.searchHere')}
            </div>
        )
    }

    public render() {
        const { showSearch } = this.state

        const selectedOption = this.getSelectedOption()
        const disabled = this.props.options.length === 0 || (this.props.options.length && this.props?.disabled)

        return (
            <div
                className={classNames('AppCore__ui__SearchableDropdown', { overlay: this.props?.overlay, disabled })}
                ref={(ref) => (this.dropdownElement = ref)}
            >
                <SelectBox
                    className="AppCore__ui__SearchableDropdown__selectedLabel"
                    open={showSearch}
                    disabled={disabled}
                    invalid={this.props.hasErrors}
                    onClick={disabled ? null : this.handleSelectedClick}
                    tabIndex={-1}
                    hideChevron={!showSearch && selectedOption != null}
                >
                    {!showSearch && selectedOption?.leftNode && (
                        <div className="AppCore__ui__SearchableDropdown__leftNode">{selectedOption.leftNode}</div>
                    )}
                    {!showSearch && (
                        <zaptic-ellipsis class="AppCore__ui__SearchableDropdown__optionLabel">
                            {selectedOption?.label ?? this.renderPlaceholder()}
                        </zaptic-ellipsis>
                    )}
                    {!disabled && !showSearch && selectedOption != null && (
                        <div className="AppCore__ui__SearchableDropdown__clear" onClick={this.onClear}>
                            <SVG_Cross />
                        </div>
                    )}

                    {showSearch && (
                        <input
                            className="AppCore__ui__SearchableDropdown__searchBox"
                            type="text"
                            ref={(ref) => (this.searchBoxElement = ref)}
                            onChange={this.handleSearchTermChange}
                            value={this.state.searchTerm}
                            onClick={(event) => event.stopPropagation()}
                            tabIndex={-1}
                        />
                    )}
                </SelectBox>
                {this.renderSearchableOptions()}
            </div>
        )
    }
}

const TreeInner = <T extends React.ReactText>(props: {
    tree: Tree<Renderable<T>>
    selectedOptionId: T
    isTopLevel: boolean
    expandedCategories: Set<T>
    onOptionClick: (optionId: T) => void
    onCategoryClick: (categoryId: T) => void
}) => {
    const optionsNumberToLoadPerPage = 100

    // use infinite loading only on first level of options tree
    const { items: loadedItems, setElementTriggeringLoadWhenVisible } = useInfiniteLoading({
        initialPageNumber: 1,
        getItems: async (pageNumber) => {
            return Result.ok({
                items: props.tree
                    .slice((pageNumber - 1) * optionsNumberToLoadPerPage, pageNumber * optionsNumberToLoadPerPage)
                    .map((treeNode) => ({ id: treeNode.data.id, node: treeNode })),
                totalPagesCount: Math.ceil(props.tree.length / optionsNumberToLoadPerPage),
            })
        },
    })

    return (
        <div className="AppCore__ui__SearchableDropdown__tree">
            {loadedItems.map(({ node }, index) => {
                return (
                    <div
                        key={node.data.id}
                        ref={
                            isLastTopLevelItem(props.isTopLevel, loadedItems.length, index)
                                ? setElementTriggeringLoadWhenVisible
                                : null
                        }
                    >
                        {(() => {
                            if (node.children.length === 0) {
                                return (
                                    <Option
                                        id={node.data.id}
                                        label={node.data.label}
                                        tagline={node.data.tagline}
                                        leftNode={node.data.leftNode}
                                        onClick={props.onOptionClick}
                                        isSelected={node.data.id === props.selectedOptionId}
                                    />
                                )
                            }

                            // Do not need separator for very first category
                            const shouldSeparatorBeShown = !props.isTopLevel || index !== 0
                            const isExpanded = props.expandedCategories.has(node.data.id)

                            return (
                                <Category
                                    id={node.data.id}
                                    label={node.data.label}
                                    subTree={node.children}
                                    expandedCategories={props.expandedCategories}
                                    isExpanded={isExpanded}
                                    shouldSeparatorBeShown={shouldSeparatorBeShown}
                                    selectedOptionId={props.selectedOptionId}
                                    onClick={props.onCategoryClick}
                                    onOptionClick={props.onOptionClick}
                                />
                            )
                        })()}
                    </div>
                )
            })}
        </div>
    )
}

const isLastTopLevelItem = (isTopLevel: boolean, itemsCount: number, itemIndex: number): boolean =>
    isTopLevel && itemsCount - 1 === itemIndex

const Option = <T extends React.ReactText>(props: {
    id: T
    label: string
    tagline?: string
    leftNode?: React.ReactNode
    isSelected: boolean
    onClick: (optionId: T) => void
}) => {
    return (
        <div
            key={props.id}
            className={classNames('AppCore__ui__SearchableDropdown__option', {
                selected: props.isSelected,
            })}
            onClick={() => props.onClick(props.id)}
        >
            {props.leftNode && <div className="AppCore__ui__SearchableDropdown__leftNode">{props.leftNode}</div>}
            <div>
                <div className="AppCore__ui__SearchableDropdown__optionLabel">{props.label}</div>
                {props.tagline && <div className="AppCore__ui__SearchableDropdown__optionTagline">{props.tagline}</div>}
            </div>
        </div>
    )
}

const Category = <T extends React.ReactText>(props: {
    id: T
    label: string
    subTree: Tree<Renderable<T>>
    shouldSeparatorBeShown: boolean
    expandedCategories: Set<T>
    isExpanded: boolean
    selectedOptionId: T
    onClick: (categoryId: T) => void
    onOptionClick: (optionId: T) => void
}) => {
    const count = getSelectableDescendantCount(props.subTree)
    if (count === 0) return null

    return (
        <div key={props.id} className="AppCore__ui__SearchableDropdown__category">
            {props.shouldSeparatorBeShown && <div className="AppCore__ui__SearchableDropdown__category-seperator" />}
            <div className="AppCore__ui__SearchableDropdown__category-item" onClick={() => props.onClick(props.id)}>
                <div
                    className={classNames('AppCore__ui__SearchableDropdown__category-chevron', {
                        expanded: props.isExpanded,
                        collapsed: !props.isExpanded,
                    })}
                >
                    <SVG_ChevronDownSmall />
                </div>
                <zaptic-ellipsis class="AppCore__ui__SearchableDropdown__category-label">{props.label}</zaptic-ellipsis>
                <div className="AppCore__ui__SearchableDropdown__category-count">{count}</div>
            </div>
            {props.isExpanded && (
                <div className="AppCore__ui__SearchableDropdown__category-children">
                    <TreeInner
                        tree={props.subTree}
                        expandedCategories={props.expandedCategories}
                        selectedOptionId={props.selectedOptionId}
                        isTopLevel={false}
                        onCategoryClick={props.onClick}
                        onOptionClick={props.onOptionClick}
                    />
                </div>
            )}
        </div>
    )
}

export function getSelectableDescendantCount<T>(tree: Tree<Renderable<T>>): number {
    return pipe(
        getLeaves(tree),
        map((n) => n.data.id),
        uniq,
        (ids) => ids.length
    )
}
